import dayjs, { Dayjs } from 'dayjs'
import { ReturnMonitoringDateType, ReturnMonitoringSearchReq } from '@/services/return/returnMonitoring/types'
import { DATE_TYPE_OPTIONS, DEFAULT_DATE_TYPE } from './constants'

/** 필터 필드 id — 그대로 폼 값 키가 된다. */
export const FIELD_NAMES = {
  shipper: 'shipper',
  dateType: 'dateType',
  dateRange: 'dateRange',
  rtnInvoiceNo: 'rtnInvoiceNo',
  actFlag: 'actFlag',
  ordNo: 'ordNo',
} as const

/** FilterArea 날짜 필드 값 — 단일/기간 어느 쪽으로 오든 받는다. */
type DateValue = Dayjs | [Dayjs | null, Dayjs | null] | null | undefined

export interface ReturnMonitoringFormValues {
  [FIELD_NAMES.shipper]?: string
  [FIELD_NAMES.dateType]?: string
  [FIELD_NAMES.dateRange]?: DateValue
  [FIELD_NAMES.rtnInvoiceNo]?: string[] | string
  [FIELD_NAMES.actFlag]?: string
  [FIELD_NAMES.ordNo]?: string[] | string
}

const DATE_FORMAT = 'YYYYMMDD'

const toDateString = (value?: Dayjs | null): string | undefined => (value?.isValid() ? value.format(DATE_FORMAT) : undefined)

const toRange = (value: DateValue): [Dayjs | null, Dayjs | null] => {
  if (!value) return [null, null]
  return Array.isArray(value) ? [value[0] ?? null, value[1] ?? null] : [value, value]
}

/** `mfs.FormTextArea` 는 배열을 주지만 단건 문자열로 올 때도 있다 — 양쪽 다 목록으로 맞춘다. */
const toTrimmedList = (values?: string[] | string): string[] => {
  if (Array.isArray(values)) return values.map((v) => String(v).trim()).filter(Boolean)
  if (typeof values === 'string') return values.trim() ? [values.trim()] : []
  return []
}

/** 조회 기준일 — 아는 값만 받는다. 빈 값·조작된 URL 값은 기본값(접수일)으로 되돌린다. */
export const toDateType = (value?: string): ReturnMonitoringDateType => DATE_TYPE_OPTIONS.find((o) => o.value === value)?.value ?? DEFAULT_DATE_TYPE

const toActFlag = (value?: string): boolean | undefined => {
  if (value === 'Y') return true
  if (value === 'N') return false
  return undefined
}

/**
 * 기본 조회기간 — 오늘 포함 최근 일주일. URL 복원 보정(`fromSearchQuery`)과 `buildDefaultValues` 가 쓴다.
 * 화면 최초 진입의 기본 기간은 생성본 뷰 `DateRangeField` 의 `presetRange('week','pastTo')` 가 따로 계산한다(같은 6일) —
 * 뷰 프리셋을 바꾸면 여기도 맞춰야 한다.
 */
export const defaultDateRange = (): [Dayjs, Dayjs] => [dayjs().subtract(6, 'day'), dayjs()]

/**
 * 화면 기본값 — 화주명 = 계정 화주, 조회 기준일 = 접수일(업로드), 조회기간 = 최근 일주일(오늘 포함 7일).
 * 조회 기준일은 FF on 일 때 사용자가 4종 중에서 고른다(LOGISPRD-12578 D-04, 1.0 `SEARCH_STATUS` 이관).
 * 기본값만 `RTN_DATE` 이고 고정값이 아니다.
 *
 * 값 자체는 FF 와 무관하게 항상 채운다 — FF 가 마운트 뒤에 늦게 도착해도(초기 false → 곧 true) 칸이
 * 빈 채로 뜨지 않게 하려는 것이다. off 면 칸이 렌더되지 않고 요청·URL 양쪽에서 잠기므로 새어 나가지 않는다.
 */
export const buildDefaultValues = (ownShipperCode: string): ReturnMonitoringFormValues => ({
  [FIELD_NAMES.shipper]: ownShipperCode,
  [FIELD_NAMES.dateType]: DEFAULT_DATE_TYPE,
  [FIELD_NAMES.dateRange]: defaultDateRange(),
  [FIELD_NAMES.rtnInvoiceNo]: [],
  [FIELD_NAMES.actFlag]: undefined,
  [FIELD_NAMES.ordNo]: [],
})

/** 화주 고정 계정 강제 — 어떤 경로로든(복원값 포함) 값이 어긋나면 자기 화주로 되돌린다(피드백 7-다). */
export const forceOwnShipper = (values: ReturnMonitoringFormValues, ownShipperCode: string): ReturnMonitoringFormValues => ({
  ...values,
  [FIELD_NAMES.shipper]: ownShipperCode,
})

/**
 * 필터 폼 값 → 조회 요청 변환. `ordStatus` 는 상태 탭이 별도로 관리해 여기서 받지 않는다.
 * 빈 값은 싣지 않는다 — BE EQUAL 조건이라 빈 문자열이 그대로 가면 무결과가 된다.
 *
 * `isDateTypeEnabled` 는 `RETURN_MONITORING_DATE_TYPE_FILTER_FLAG` 다. off 면 폼에 무슨 값이 남아 있든
 * `RTN_DATE` 로 고정한다 — 실제 요청이 나가는 경계라 여기서 잠가야 as-is 가 담보된다. 기본값을 off 로
 * 두어 인자를 빠뜨린 호출부가 새 동작이 아니라 as-is 로 떨어지게 한다.
 */
export const toReturnMonitoringReq = (
  values: ReturnMonitoringFormValues,
  isDateTypeEnabled = false,
): Omit<ReturnMonitoringSearchReq, 'ordStatus'> => {
  const [dateFromDayjs, dateToDayjs] = toRange(values[FIELD_NAMES.dateRange])
  const dateFrom = toDateString(dateFromDayjs)
  const dateTo = toDateString(dateToDayjs)
  const rtnInvoiceNo = toTrimmedList(values[FIELD_NAMES.rtnInvoiceNo])
  const ordNo = toTrimmedList(values[FIELD_NAMES.ordNo])
  const actFlag = toActFlag(values[FIELD_NAMES.actFlag])

  return {
    dateType: isDateTypeEnabled ? toDateType(values[FIELD_NAMES.dateType]) : DEFAULT_DATE_TYPE,
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
    ...(values[FIELD_NAMES.shipper] ? { shipper: values[FIELD_NAMES.shipper] } : {}),
    ...(rtnInvoiceNo.length > 0 ? { rtnInvoiceNo } : {}),
    ...(actFlag === undefined ? {} : { actFlag }),
    ...(ordNo.length > 0 ? { ordNo } : {}),
  }
}

/** 필수 필터 — 화주명과 조회기간(양끝) 모두 있어야 조회한다(셸 필수 표시 2종). */
export const hasRequiredFilter = (values: ReturnMonitoringFormValues): boolean => {
  const [from, to] = toRange(values[FIELD_NAMES.dateRange])
  return !!values[FIELD_NAMES.shipper] && !!from?.isValid() && !!to?.isValid()
}
